<template>
    <div>
        <h2>Plan Your Trip Budget</h2>
        <p v-if="loadError" class="calc-error">Could not load expressway data. Please refresh the page.</p>
        <p v-else-if="!expressways.length" class="calc-loading">Loading toll points…</p>

        <template v-else>
            <div class="field">
                <label>Vehicle class</label>
                <div class="class-cards">
                    <button
                        v-for="c in classes"
                        :key="c.value"
                        type="button"
                        :class="{ selected: vehicleClass === c.value }"
                        @click="pickClass(c.value)"
                    >
                        <svg class="ico" aria-hidden="true"><use :href="'#i-' + String(c.icon).slice(3)"></use></svg>
                        <span class="class-name">Class {{ c.value }}</span>
                        <span class="class-desc">{{ c.desc }}</span>
                    </button>
                </div>
            </div>

            <div v-for="(leg, i) in legs" :key="i" class="leg-card">
                <div class="leg-head">
                    <strong>Leg {{ i + 1 }}<template v-if="leg.entry"> · {{ leg.entry.code }}</template></strong>
                    <button v-if="legs.length > 1" type="button" class="leg-remove" @click="removeLeg(i)">
                        <svg class="ico" aria-hidden="true"><use href="#i-xmark"></use></svg> Remove
                    </button>
                </div>
                <div class="leg-points">
                    <PointAutocomplete
                        v-model="leg.entry"
                        label="Entry point"
                        :input-id="`tp-entry-${i}`"
                        placeholder="e.g. Balintawak…"
                        :points="allPoints"
                        :restrict-expressway-id="leg.exit?.expresswayId ?? null"
                        :exclude-id="leg.exit?.id ?? null"
                        @update:model-value="legChanged(leg)"
                    />
                    <PointAutocomplete
                        v-model="leg.exit"
                        label="Exit point"
                        :input-id="`tp-exit-${i}`"
                        placeholder="e.g. Sta. Ines…"
                        :points="allPoints"
                        :restrict-expressway-id="leg.entry?.expresswayId ?? null"
                        :exclude-id="leg.entry?.id ?? null"
                        @update:model-value="legChanged(leg)"
                    />
                </div>
                <button type="button" class="leg-swap" @click="swapLeg(leg)" :disabled="!leg.entry && !leg.exit">
                    <svg class="ico" aria-hidden="true"><use href="#i-arrows-up-down"></use></svg> Swap
                </button>
                <p v-if="leg.error" class="calc-error">{{ leg.error }}</p>
            </div>

            <button type="button" class="swap-btn" @click="addLeg">
                <svg class="ico" aria-hidden="true"><use href="#i-plus"></use></svg> Add another leg (e.g. NLEX → SCTEX → TPLEX)
            </button>

            <details class="fuel-extra">
                <summary>Add fuel estimate (optional)</summary>
                <div class="leg-points fuel-grid">
                    <div class="field">
                        <label>Total distance (km)</label>
                        <input type="number" min="1" v-model.number="fuelKm" placeholder="e.g. 250">
                    </div>
                    <div class="field">
                        <label>km per liter</label>
                        <input type="number" min="1" step="0.1" v-model.number="fuelEff" placeholder="e.g. 12">
                    </div>
                    <div class="field">
                        <label>₱ per liter</label>
                        <input type="number" min="1" step="0.01" v-model.number="fuelPrice" placeholder="e.g. 62.50">
                    </div>
                </div>
            </details>

            <div class="field" style="margin-top:12px;">
                <label>Trip type</label>
                <div class="class-picker">
                    <button type="button" :class="{ selected: !roundTrip }" @click="pickTrip(false)">One way</button>
                    <button type="button" :class="{ selected: roundTrip }" @click="pickTrip(true)">Round trip</button>
                </div>
            </div>

            <div class="calc-actions">
                <button type="button" class="btn" @click="compute" :disabled="!canCompute || computing">
                    {{ computing ? 'Computing…' : 'Compute Trip Budget' }}
                </button>
                <button type="button" class="btn btn-ghost" @click="clearAll">Clear All</button>
            </div>

            <div v-if="computed_" class="result-card">
                <div class="result-top">
                    <div class="result-info">
                        <h3 class="result-route">
                            <svg class="ico" aria-hidden="true"><use href="#i-map-location-dot"></use></svg>
                            {{ tripStart }} <svg class="ico result-arrow" aria-hidden="true"><use href="#i-arrow-right-long"></use></svg> {{ tripEnd }}
                        </h3>
                        <div class="result-chips">
                            <span class="result-chip"><svg class="ico" aria-hidden="true"><use href="#i-route"></use></svg> {{ legLabel }}</span>
                            <span class="result-chip"><svg class="ico" aria-hidden="true"><use href="#i-road"></use></svg> {{ tripCodes }}</span>
                            <span v-if="fuelCost" class="result-chip"><svg class="ico" aria-hidden="true"><use href="#i-gas-pump"></use></svg> fuel included</span>
                        </div>
                        <div class="trip-toggle">
                            <button type="button" :class="{ selected: !roundTrip }" @click="pickTrip(false)">One way</button>
                            <button type="button" :class="{ selected: roundTrip }" @click="pickTrip(true)">Round trip</button>
                        </div>
                    </div>
                    <div class="result-fee">
                        <span class="fee-label">Total Trip Cost</span>
                        <span class="fee-amount">₱{{ fmt(grandTotal) }}</span>
                        <span class="fee-sub">Class {{ vehicleClass }} · {{ roundTrip ? 'round trip' : 'one way' }}</span>
                        <span v-for="(total, network) in rfidTotals" :key="network" class="fee-balance">
                            <svg class="ico" aria-hidden="true"><use href="#i-credit-card"></use></svg>
                            {{ network }} load: <strong>₱{{ fmt(suggestLoad(total)) }}</strong>
                        </span>
                    </div>
                </div>
            </div>

            <details v-if="computed_" class="route-details">
                <summary><svg class="ico" aria-hidden="true"><use href="#i-list-ul"></use></svg> View Trip Breakdown</summary>
                <table class="rate-rows">
                    <tbody>
                        <tr v-for="(leg, i) in validLegs" :key="i">
                            <td><svg class="ico" aria-hidden="true"><use href="#i-road"></use></svg> {{ legName(leg) }}</td>
                            <td>₱{{ fmt(tripFee(leg.fee)) }}</td>
                        </tr>
                        <tr v-if="fuelCost"><td><svg class="ico" aria-hidden="true"><use href="#i-gas-pump"></use></svg> Fuel ({{ liters }} L)</td><td>₱{{ fmt(fuelCost) }}</td></tr>
                        <tr class="rate-row-selected"><td><strong>Total</strong></td><td><strong>₱{{ fmt(grandTotal) }}</strong></td></tr>
                    </tbody>
                </table>
                <p class="calc-note" style="margin-top:8px;">
                    RFID loads include a 20% buffer, rounded up to ₱50. VAT-inclusive reference rates — fees at the plaza prevail.
                </p>
            </details>

            <div v-if="computed_" class="result-actions result-actions-bar">
                <button type="button" @click="copySummary">
                    <svg class="ico" aria-hidden="true"><use href="#i-copy"></use></svg> {{ copied ? '✓ Copied!' : 'Copy Summary' }}
                </button>
                <button type="button" @click="share">
                    <svg class="ico" aria-hidden="true"><use href="#i-share-nodes"></use></svg> {{ sharedOk ? '✓ Shared!' : 'Share' }}
                </button>
                <a v-if="directionsUrl" :href="directionsUrl" target="_blank" rel="noopener" @click="resultAction('directions')">
                    <svg class="ico" aria-hidden="true"><use href="#i-diamond-turn-right"></use></svg> Directions to {{ tripEnd }}
                </a>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { track, trackOnce } from '../analytics';
import PointAutocomplete from './PointAutocomplete.vue';

const expressways = ref([]);
const loadError = ref(false);
const vehicleClass = ref(1);
const roundTrip = ref(false);
const legs = ref([newLeg()]);
const computing = ref(false);
const computed_ = ref(false);
const copied = ref(false);
const fuelKm = ref(null);
const fuelEff = ref(null);
const fuelPrice = ref(null);

const classes = [
    { value: 1, icon: 'fa-car-side', desc: 'Cars, SUVs & vans' },
    { value: 2, icon: 'fa-bus', desc: 'Buses & light trucks' },
    { value: 3, icon: 'fa-truck-moving', desc: 'Heavy trucks & trailers' },
];

function newLeg() {
    return { entry: null, exit: null, fee: null, rates: null, error: '' };
}

onMounted(async () => {
    try {
        const res = await fetch('/api/calculator/expressways');
        if (!res.ok) throw new Error();
        expressways.value = await res.json();
    } catch {
        loadError.value = true;
    }
});

const allPoints = computed(() => expressways.value.flatMap((e) =>
    e.points.map((p) => ({
        id: p.id,
        name: p.name,
        expresswayId: e.id,
        code: e.code,
        expresswayName: e.name,
    }))
));

function pickClass(value) {
    vehicleClass.value = value;
    recompute();
    track('calc_interaction', { tool: 'planner', action: 'vehicle_class', vehicle_class: value });
}

function pickTrip(value) {
    roundTrip.value = value;
    track('calc_interaction', { tool: 'planner', action: 'round_trip', round_trip: value });
}

function resultAction(action) {
    track('result_action', {
        tool: 'planner',
        action,
        legs: validLegs.value.length,
        expressway: tripCodes.value,
        vehicle_class: vehicleClass.value,
    });
}

function legChanged(leg) {
    trackOnce('start:planner', 'calc_start', { tool: 'planner' });
    leg.fee = null;
    leg.rates = null;
    leg.error = '';
    computed_.value = false;
}

function swapLeg(leg) {
    [leg.entry, leg.exit] = [leg.exit, leg.entry];
    legChanged(leg);
}

function addLeg() {
    legs.value.push(newLeg());
    computed_.value = false;
    // Multi-leg use is the whole point of this tool; worth knowing how often
    // anyone actually reaches for it.
    track('calc_interaction', { tool: 'planner', action: 'add_leg', legs: legs.value.length });
}

function removeLeg(i) {
    legs.value.splice(i, 1);
    computed_.value = false;
    track('calc_interaction', { tool: 'planner', action: 'remove_leg', legs: legs.value.length });
}

function clearAll() {
    legs.value = [newLeg()];
    vehicleClass.value = 1;
    roundTrip.value = false;
    fuelKm.value = null;
    fuelEff.value = null;
    fuelPrice.value = null;
    computed_.value = false;
    track('calc_interaction', { tool: 'planner', action: 'clear' });
}

const canCompute = computed(() =>
    legs.value.some((l) => l.entry && l.exit && l.entry.id !== l.exit.id)
);

const validLegs = computed(() => legs.value.filter((l) => l.fee !== null));

async function compute() {
    computing.value = true;
    computed_.value = false;
    for (const leg of legs.value) {
        leg.error = '';
        leg.fee = null;
        if (!leg.entry || !leg.exit || leg.entry.id === leg.exit.id) continue;
        try {
            const params = new URLSearchParams({ entry: leg.entry.id, exit: leg.exit.id });
            const res = await fetch(`/api/calculator/rate?${params}`);
            if (!res.ok) {
                leg.error = 'No published rate for this entry/exit pair.';
                continue;
            }
            const data = await res.json();
            leg.rates = data.rates;
            leg.fee = data.rates[String(vehicleClass.value)] ?? null;
            if (leg.fee === null) leg.error = 'No rate published for this vehicle class on this pair.';
        } catch {
            leg.error = 'Could not compute this leg.';
        }
    }
    computed_.value = validLegs.value.length > 0;
    computing.value = false;

    const attempted = legs.value.filter((l) => l.entry && l.exit && l.entry.id !== l.exit.id).length;

    track('trip_plan_compute', {
        status: computed_.value ? (validLegs.value.length === attempted ? 'success' : 'partial') : 'no_rate',
        legs: attempted,
        legs_priced: validLegs.value.length,
        expressway: tripCodes.value,
        vehicle_class: vehicleClass.value,
        round_trip: roundTrip.value,
        toll_total: tollTotal.value,
        fuel_total: fuelCost.value,
        total: grandTotal.value,
    });
}

function recompute() {
    for (const leg of legs.value) {
        if (leg.rates) leg.fee = leg.rates[String(vehicleClass.value)] ?? null;
    }
}

const tripFee = (fee) => (roundTrip.value ? fee * 2 : fee);
const tollTotal = computed(() => validLegs.value.reduce((sum, l) => sum + tripFee(l.fee), 0));

const liters = computed(() => {
    if (!(fuelKm.value > 0 && fuelEff.value > 0)) return 0;
    const km = roundTrip.value ? fuelKm.value * 2 : fuelKm.value;
    return Math.round((km / fuelEff.value) * 10) / 10;
});
const fuelCost = computed(() => (liters.value && fuelPrice.value > 0 ? Math.round(liters.value * fuelPrice.value) : 0));
const grandTotal = computed(() => tollTotal.value + fuelCost.value);

const rfidTotals = computed(() => {
    const totals = {};
    for (const leg of validLegs.value) {
        const rfid = expressways.value.find((e) => e.id === leg.entry.expresswayId)?.rfid || 'RFID';
        totals[rfid] = (totals[rfid] || 0) + tripFee(leg.fee);
    }
    return totals;
});

const suggestLoad = (total) => Math.ceil((total * 1.2) / 50) * 50;

const legLabel = computed(() => `${validLegs.value.length} leg${validLegs.value.length === 1 ? '' : 's'}`);

const tripStart = computed(() => validLegs.value[0]?.entry.name ?? '');
const tripEnd = computed(() => validLegs.value[validLegs.value.length - 1]?.exit.name ?? '');
const tripCodes = computed(() => [...new Set(validLegs.value.map((l) => l.entry.code))].join(' + '));

const sharedOk = ref(false);

// Google Maps directions from the user's current location to the final exit.
const directionsUrl = computed(() => {
    const lastLeg = validLegs.value[validLegs.value.length - 1];
    if (!lastLeg) return null;
    const e = expressways.value.find((x) => x.id === lastLeg.exit.expresswayId);
    const destination = `${lastLeg.exit.name} Exit, ${e?.name ?? ''}, Philippines`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;
});

async function share() {
    const text = `${tripStart.value} → ${tripEnd.value} (${tripCodes.value}): ₱${fmt(grandTotal.value)} total, Class ${vehicleClass.value}${roundTrip.value ? ', round trip' : ''}`;
    try {
        if (navigator.share) {
            await navigator.share({ title: 'Trip Budget', text, url: window.location.href });
        } else {
            await navigator.clipboard.writeText(`${text} — ${window.location.href}`);
        }
        sharedOk.value = true;
        resultAction(navigator.share ? 'share' : 'share_copy');
        setTimeout(() => (sharedOk.value = false), 2000);
    } catch { /* cancelled */ }
}

function legName(leg) {
    return `${leg.entry.code}: ${leg.entry.name} → ${leg.exit.name}`;
}

const fmt = (v) => new Intl.NumberFormat('en-PH').format(v);

async function copySummary() {
    const lines = validLegs.value.map((l) => `${legName(l)}: ₱${fmt(tripFee(l.fee))}`);
    if (fuelCost.value) lines.push(`Fuel: ₱${fmt(fuelCost.value)}`);
    lines.push(`Total (${roundTrip.value ? 'round trip' : 'one way'}, Class ${vehicleClass.value}): ₱${fmt(grandTotal.value)}`);
    for (const [network, total] of Object.entries(rfidTotals.value)) {
        lines.push(`${network} load suggestion: ₱${fmt(suggestLoad(total))}`);
    }
    lines.push(`via ${window.location.origin}`);
    try {
        await navigator.clipboard.writeText(lines.join('\n'));
        copied.value = true;
        resultAction('copy');
        setTimeout(() => (copied.value = false), 2000);
    } catch { /* ignore */ }
}
</script>
